import React, { useState, useEffect, useMemo, useCallback } from 'react';
import '../../mentorComponents/labPractice/LabPractice.css';
import {
  Box, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress,
  FormHelperText, TablePagination, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Typography, Autocomplete, TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { toast } from 'react-toastify';
import { getAllCOE } from '../../../services/adminServices/CoeServices';
import { getAllCoeLabs } from '../../../services/mentorServices/DailyPracticeServices';
import { getAllMakerPlansByLab } from '../../../services/adminServices/MakerPlanServices';
import { getLabWiseModulePractice } from '../../../services/reportServices/adminReportServices/LabWiseReportServices';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#7e7e7e",
    color: theme.palette.common.white,
    fontSize: 16,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const LabwiseModule = () => {
  const [selection, setSelection] = useState({ coe: '', lab: null, makerPlan: '' });
  const [errors, setErrors] = useState({});
  const [tableData, setTableData] = useState([]);
  const [coes, setCoes] = useState([]);
  const [labs, setLabs] = useState([]);
  const [makerPlans, setMakerPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    getAllCOE().then(res => {
      const sortedLabs = res.data.data?.sort((a, b) => a.name?.localeCompare(b.name)) || [];
      setCoes(sortedLabs || [])
    }).catch(error => toast.error(error?.response?.data?.message || error.message || 'Error fetching COEs'));
  }, []);

  const handleSelectionChange = async ({ target: { name, value } }) => {
    setSelection((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'coe') {
      setSelection(prev => ({ ...prev, lab: null, makerPlan: '' }));
      try {
        const res = await getAllCoeLabs(value);
        const sortedLabs = res.data.data?.sort((a, b) => a.lab_name?.localeCompare(b.lab_name)) || [];
        setLabs(sortedLabs || []);
      } catch (error) {
        setLabs([]);
        toast.error(error?.response?.data?.message || error.message || 'Error fetching labs');
      }
    }
  };

  const handleLabChange = async (event, value) => {
    setSelection(prev => ({ ...prev, lab: value, makerPlan: '' }));
    setErrors(prev => ({ ...prev, lab: '' }));

    if (value?.id) {
      try {
        const res = await getAllMakerPlansByLab(value.id);
        setMakerPlans(res.data.data || []);
      } catch (error) {
        setMakerPlans([]);
        toast.error(error?.response?.data?.message || error.message || 'Error fetching maker plans');
      }
    }
  };
  useEffect(() => {
    if (selection.coe && selection.lab && selection.makerPlan) {
      setDisableButton(false)
    } else {
      setDisableButton(true)
    }
  }, [selection])

  const validateSelections = () => {
    const newErrors = {
      coe: selection.coe ? '' : 'Please select a COE',
      lab: selection.lab ? '' : 'Please select a Lab',
      makerPlan: selection.makerPlan ? '' : 'Please select a Maker Plan',
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const fetchData = async () => {
    if (!validateSelections()) return;
    setDisableButton(true);
    setLoading(true);

    try {
      const res = await getLabWiseModulePractice(selection.coe, selection.lab.id, selection.makerPlan);
      setTableData(res.data.data?.data || []);
      setShowTable(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Error fetching report');
      setTableData([]);
    } finally {
      setLoading(false);
      setDisableButton(false);
    }
  };

  const sortedData = useMemo(() => [...tableData].sort((a, b) => new Date(b.AttendanceDate) - new Date(a.AttendanceDate)), [tableData]);
  const paginatedRows = useMemo(() => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [sortedData, page, rowsPerPage]);

  const handleChangePage = useCallback((_, newPage) => setPage(newPage), []);
  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }, []);

  return (
    <Box className="dashboard-container" sx={{ justifyContent: 'start', alignItems: 'start' }}>
      <Typography className="dashboard-header-title" pt={2} pb={2} width="100%">
        Welcome {JSON.parse(localStorage.getItem("user"))?.name || "User"}! to the Lab Wise Module Practice Report.
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={2} sx={{ width: { xs: '100%', md: '100%' } }}>
        <FormControl size="small" sx={{ width: '25%' }} error={!!errors.coe}>
          <InputLabel>COE*</InputLabel>
          <Select name="coe" label="COE" value={selection.coe} onChange={handleSelectionChange}>
            {coes.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
            ))}
          </Select>
          {errors.coe && <FormHelperText>{errors.coe}</FormHelperText>}
        </FormControl>

        <Autocomplete
          options={labs}
          getOptionLabel={(option) => option.lab_name || ''}
          value={selection.lab}
          onChange={handleLabChange}
          renderInput={(params) => (
            <TextField {...params} label="Lab*" size="small" error={!!errors.lab} helperText={errors.lab} />
          )}
          sx={{ width: '25%' }}
        />

        <FormControl size="small" sx={{ width: '25%' }} error={!!errors.makerPlan}>
          <InputLabel>Maker Plan*</InputLabel>
          <Select
            name="makerPlan"
            label="Maker Plan"
            value={selection.makerPlan}
            onChange={handleSelectionChange}
          >
            {makerPlans.map((opt) => (
              <MenuItem key={opt.makerplanID} value={opt.makerplanID}>{opt.makerplan_name}</MenuItem>
            ))}
          </Select>
          {errors.makerPlan && <FormHelperText>{errors.makerPlan}</FormHelperText>}
        </FormControl>

        <Button variant="contained" onClick={fetchData} disabled={loading || disableButton} sx={{ height: "100% !important" }}>
          {loading ? <CircularProgress size={24} /> : 'VIEW'}
        </Button>
      </Box>

      <Box className="dashboard-box-container">
        {showTable && (
          <TableContainer component={Paper} className="table-container" sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: '100%' }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Lab Name</StyledTableCell>
                  <StyledTableCell>Lab Strength</StyledTableCell>
                  {tableData[0]?.modules?.map((mod, idx) => (
                    <StyledTableCell key={idx}>#{mod.name} Started</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.length > 0 ? paginatedRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.lab_name || '-'}</TableCell>
                    <TableCell>{row.lab_strength || 0}</TableCell>
                    {row.modules.map((mod, idx) => (
                      <TableCell key={idx}>{mod.started}</TableCell>
                    ))}
                  </TableRow>
                )) : (
                  <TableRow>
                    <StyledTableCell colSpan={5} align="center">No records found.</StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10,20,30,60,100]}
              component="div"
              count={tableData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default LabwiseModule;